import { createFileRoute, Link } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import {
	ArrowLeft,
	Check,
	Lightbulb,
	Pencil,
	Sparkles,
	X,
} from "lucide-react";
import { toast } from "react-toastify";

import type {
	AcceptSuggestionRequest,
	FilterRules,
	GroupSuggestion,
} from "@/types/action-groups";
import { apiService } from "@/lib/api-service";
import { useSession } from "@/hooks/use-session";
import { cn } from "@/lib/utils";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { ReadWriteOnly } from "@/components/common/read-write-only";
import {
	Card,
	CardContent,
	CardDescription,
	CardFooter,
	CardHeader,
	CardTitle,
} from "@/components/ui/card";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Spinner } from "@/components/ui/spinner";
import { Textarea } from "@/components/ui/textarea";
import { FilterRuleBuilder } from "@/components/action-groups/filter-rule-builder";

export const Route = createFileRoute("/_app/_/action-groups/suggestions")({
	component: AISuggestionsPage,
});

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function priorityBadgeClass(priority: GroupSuggestion["priority"]) {
	switch (priority) {
		case "high":
			return "bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300";
		case "medium":
			return "bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300";
		case "low":
			return "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300";
	}
}

// ---------------------------------------------------------------------------
// Main Page Component
// ---------------------------------------------------------------------------

function AISuggestionsPage() {
	const session = useSession();
	const campaignId = session.campaign?.id;
	const queryClient = useQueryClient();

	// Track which suggestion indices have been acted on (accepted or dismissed)
	const [acceptedIndices, setAcceptedIndices] = useState<Set<number>>(
		new Set(),
	);
	const [dismissedIndices, setDismissedIndices] = useState<Set<number>>(
		new Set(),
	);

	// Edit dialog state
	const [editIndex, setEditIndex] = useState<number | null>(null);

	// Fetch existing suggestion session
	const {
		data: sessionData,
		isLoading: sessionLoading,
		error: sessionError,
	} = useQuery({
		queryKey: ["ai-suggestions", campaignId],
		queryFn: () => apiService.getSuggestions(campaignId!),
		enabled: !!campaignId,
		retry: false,
	});

	// Generate suggestions mutation
	const generateMutation = useMutation({
		mutationFn: () => apiService.generateSuggestions(campaignId!),
		onSuccess: () => {
			queryClient.invalidateQueries({
				queryKey: ["ai-suggestions", campaignId],
			});
		},
		onError: (error) => {
			toast.error(
				error instanceof Error
					? error.message
					: "Failed to generate suggestions.",
			);
		},
	});

	// Accept suggestion mutation
	const acceptMutation = useMutation({
		mutationFn: ({ index }: { index: number }) =>
			apiService.acceptSuggestion(
				campaignId!,
				sessionData!.id,
				index,
			),
		onSuccess: (_data, variables) => {
			setAcceptedIndices((prev) => new Set(prev).add(variables.index));
			toast.success("Group created from suggestion.");
			queryClient.invalidateQueries({
				queryKey: ["action-groups", campaignId],
			});
		},
		onError: (error) => {
			toast.error(
				error instanceof Error
					? error.message
					: "Failed to accept suggestion.",
			);
		},
	});

	// Edit suggestion mutation
	const editMutation = useMutation({
		mutationFn: ({
			index,
			data,
		}: {
			index: number;
			data: AcceptSuggestionRequest;
		}) =>
			apiService.editSuggestion(
				campaignId!,
				sessionData!.id,
				index,
				data,
			),
		onSuccess: (_data, variables) => {
			setAcceptedIndices((prev) => new Set(prev).add(variables.index));
			setEditIndex(null);
			toast.success("Group created with your edits.");
			queryClient.invalidateQueries({
				queryKey: ["action-groups", campaignId],
			});
		},
		onError: (error) => {
			toast.error(
				error instanceof Error
					? error.message
					: "Failed to save suggestion edits.",
			);
		},
	});

	// Dismiss suggestion mutation
	const dismissMutation = useMutation({
		mutationFn: ({ index }: { index: number }) =>
			apiService.dismissSuggestion(
				campaignId!,
				sessionData!.id,
				index,
			),
		onSuccess: (_data, variables) => {
			setDismissedIndices((prev) => new Set(prev).add(variables.index));
			toast.success("Suggestion dismissed.");
		},
		onError: (error) => {
			toast.error(
				error instanceof Error
					? error.message
					: "Failed to dismiss suggestion.",
			);
		},
	});

	// Reset acted-on indices when the session changes
	useEffect(() => {
		setAcceptedIndices(new Set());
		setDismissedIndices(new Set());
	}, [sessionData?.id]);

	if (!campaignId) {
		return (
			<div className="min-h-screen bg-muted/30 p-6">
				<div className="mx-auto max-w-4xl space-y-6">
					<p className="text-muted-foreground">
						No campaign found. Please complete onboarding first.
					</p>
				</div>
			</div>
		);
	}

	const hasSession =
		sessionData && sessionData.suggestions && !sessionError;
	const isGenerating =
		generateMutation.isPending ||
		(hasSession && sessionData.status === "generating");
	const hasError = hasSession && sessionData.error;
	const suggestions = sessionData?.suggestions ?? [];

	// Filter out dismissed suggestions for the visible list
	const visibleSuggestions = suggestions
		.map((s, i) => ({ suggestion: s, index: i }))
		.filter(({ index }) => !dismissedIndices.has(index));

	const allResolved =
		hasSession &&
		suggestions.length > 0 &&
		suggestions.every(
			(_, i) => acceptedIndices.has(i) || dismissedIndices.has(i),
		);

	return (
		<div className="min-h-screen bg-muted/30 p-6">
			<div className="mx-auto max-w-4xl space-y-6">
				{/* Back link */}
				<Button variant="ghost" size="sm" asChild>
					<Link to="/action-groups">
						<ArrowLeft
							data-icon="inline-start"
							className="size-4"
						/>
						Back to Action Groups
					</Link>
				</Button>

				{/* Header */}
				<div className="flex items-center justify-between">
					<div>
						<h1 className="text-2xl font-semibold">
							AI Group Suggestions
						</h1>
						<p className="text-sm text-muted-foreground">
							Let AI analyze your contacts and suggest targeted
							action groups
						</p>
					</div>
					{hasSession &&
						!isGenerating &&
						sessionData.status === "completed" && (
							<ReadWriteOnly message="Read-only — you can't generate"><Button
								variant="outline"
								onClick={() => generateMutation.mutate()}
								disabled={generateMutation.isPending}
							>
								<Sparkles
									data-icon="inline-start"
									className="size-4"
								/>
								Regenerate
							</Button></ReadWriteOnly>
						)}
				</div>

				{/* Loading existing session */}
				{sessionLoading && (
					<div className="flex items-center justify-center py-20">
						<Spinner className="size-6" />
					</div>
				)}

				{/* No session yet -- prompt to generate */}
				{!sessionLoading && !hasSession && !isGenerating && (
					<div className="rounded-lg border border-dashed p-12 text-center">
						<Sparkles className="mx-auto mb-4 size-10 text-muted-foreground/50" />
						<h3 className="text-lg font-medium">
							No suggestions yet
						</h3>
						<p className="mt-1 text-sm text-muted-foreground">
							Generate group suggestions based on your
							imported contacts.
						</p>
						<ReadWriteOnly message="Read-only — you can't generate"><Button
							className="mt-4"
							onClick={() => generateMutation.mutate()}
							disabled={generateMutation.isPending}
						>
							<Sparkles
								data-icon="inline-start"
								className="size-4"
							/>
							Generate Suggestions
						</Button></ReadWriteOnly>
					</div>
				)}

				{/* Generating state */}
				{isGenerating && (
					<div className="flex flex-col items-center justify-center gap-4 rounded-lg border border-dashed p-12">
						<Spinner className="size-8" />
						<div className="text-center">
							<h3 className="text-lg font-medium">
								Analyzing your contacts...
							</h3>
							<p className="mt-1 text-sm text-muted-foreground">
								AI is reviewing your contact data and building
								targeted group suggestions. This may take a
								moment.
							</p>
						</div>
					</div>
				)}

				{/* Error state */}
				{hasError && !isGenerating && (
					<div className="rounded-lg border border-destructive/50 bg-destructive/5 p-6 text-center">
						<p className="text-destructive">
							{sessionData.error ||
								"Failed to generate suggestions. Please try again."}
						</p>
						<ReadWriteOnly message="Read-only — you can't generate"><Button
							className="mt-4"
							variant="outline"
							onClick={() => generateMutation.mutate()}
							disabled={generateMutation.isPending}
						>
							Retry
						</Button></ReadWriteOnly>
					</div>
				)}

				{/* Empty suggestions */}
				{hasSession &&
					!isGenerating &&
					!hasError &&
					sessionData.status === "completed" &&
					suggestions.length === 0 && (
						<div className="rounded-lg border border-dashed p-12 text-center">
							<Lightbulb className="mx-auto mb-4 size-10 text-muted-foreground/50" />
							<h3 className="text-lg font-medium">
								No suggestions available
							</h3>
							<p className="mt-1 text-sm text-muted-foreground">
								AI could not find meaningful groupings for your
								contacts. Try importing more contacts with
								enriched data and generating again.
							</p>
						</div>
					)}

				{/* All resolved state */}
				{allResolved && (
					<div className="rounded-lg border border-green-200 bg-green-50 p-8 text-center dark:border-green-900/50 dark:bg-green-900/20">
						<Check className="mx-auto mb-3 size-8 text-green-600 dark:text-green-400" />
						<h3 className="text-lg font-medium text-green-800 dark:text-green-300">
							All suggestions reviewed
						</h3>
						<p className="mt-1 text-sm text-green-700 dark:text-green-400">
							You have reviewed all AI suggestions. Accepted
							groups are now available in your action groups list.
						</p>
						<Button className="mt-4" variant="outline" asChild>
							<Link to="/action-groups">
								View Action Groups
							</Link>
						</Button>
					</div>
				)}

				{/* Suggestion Cards */}
				{hasSession &&
					!isGenerating &&
					!hasError &&
					sessionData.status === "completed" &&
					!allResolved &&
					visibleSuggestions.length > 0 && (
						<div className="space-y-4">
							{visibleSuggestions.map(
								({ suggestion, index }) => (
									<SuggestionCard
										key={index}
										suggestion={suggestion}
										index={index}
										isAccepted={acceptedIndices.has(index)}
										isAccepting={
											acceptMutation.isPending &&
											acceptMutation.variables?.index ===
												index
										}
										isDismissing={
											dismissMutation.isPending &&
											dismissMutation.variables
												?.index === index
										}
										onAccept={() =>
											acceptMutation.mutate({ index })
										}
										onEdit={() => setEditIndex(index)}
										onDismiss={() =>
											dismissMutation.mutate({ index })
										}
									/>
								),
							)}
						</div>
					)}
			</div>

			{/* Edit Dialog */}
			{editIndex !== null && sessionData && suggestions[editIndex] && (
				<EditSuggestionDialog
					campaignId={campaignId}
					suggestion={suggestions[editIndex]}
					open={editIndex !== null}
					onOpenChange={(open) => {
						if (!open) setEditIndex(null);
					}}
					isPending={editMutation.isPending}
					onSave={(data) =>
						editMutation.mutate({ index: editIndex, data })
					}
				/>
			)}
		</div>
	);
}

// ---------------------------------------------------------------------------
// Suggestion Card
// ---------------------------------------------------------------------------

interface SuggestionCardProps {
	suggestion: GroupSuggestion;
	index: number;
	isAccepted: boolean;
	isAccepting: boolean;
	isDismissing: boolean;
	onAccept: () => void;
	onEdit: () => void;
	onDismiss: () => void;
}

function SuggestionCard({
	suggestion,
	index,
	isAccepted,
	isAccepting,
	isDismissing,
	onAccept,
	onEdit,
	onDismiss,
}: SuggestionCardProps) {
	return (
		<Card
			className={cn(
				isAccepted &&
					"border-green-200 bg-green-50/50 dark:border-green-900/50 dark:bg-green-900/10",
			)}
			data-testid={`suggestion-card-${index}`}
		>
			<CardHeader>
				<div className="flex items-start justify-between">
					<div className="space-y-1">
						<CardTitle className="text-lg">
							{suggestion.name}
						</CardTitle>
						{suggestion.description && (
							<CardDescription>
								{suggestion.description}
							</CardDescription>
						)}
					</div>
					<div className="flex items-center gap-2">
						{isAccepted && (
							<Badge className="bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300">
								<Check className="mr-1 size-3" />
								Created
							</Badge>
						)}
						<Badge
							variant="secondary"
							className={priorityBadgeClass(suggestion.priority)}
						>
							{suggestion.priority} priority
						</Badge>
					</div>
				</div>
			</CardHeader>

			<CardContent className="space-y-3">
				{/* Metrics row */}
				<div className="flex items-center gap-3">
					<Badge variant="outline">
						{suggestion.expected_count}{" "}
						{suggestion.expected_count === 1
							? "contact"
							: "contacts"}
					</Badge>
					<Badge
						variant="secondary"
						className="bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300"
					>
						Dynamic (auto-updates)
					</Badge>
				</div>

				{/* AI explanation */}
				<p className="text-sm text-muted-foreground">
					{suggestion.explanation}
				</p>

				{/* Enrichment suggestion banner */}
				{suggestion.enrichment_suggestion && (
					<div className="flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 dark:border-amber-900/50 dark:bg-amber-900/20">
						<Lightbulb className="mt-0.5 size-4 shrink-0 text-amber-600 dark:text-amber-400" />
						<p className="text-sm text-amber-700 dark:text-amber-300">
							{suggestion.enrichment_suggestion}
						</p>
					</div>
				)}
			</CardContent>

			{!isAccepted && (
				<CardFooter>
					<div className="flex items-center gap-2">
						<ReadWriteOnly message="Read-only — you can't apply suggestions"><Button
							size="sm"
							onClick={onAccept}
							disabled={isAccepting || isDismissing}
						>
							{isAccepting ? (
								<>
									<Spinner className="size-4" />
									Accepting...
								</>
							) : (
								<>
									<Check
										data-icon="inline-start"
										className="size-4"
									/>
									Accept
								</>
							)}
						</Button></ReadWriteOnly>
						<Button
							variant="outline"
							size="sm"
							onClick={onEdit}
							disabled={isAccepting || isDismissing}
						>
							<Pencil
								data-icon="inline-start"
								className="size-4"
							/>
							Edit
						</Button>
						<Button
							variant="ghost"
							size="sm"
							onClick={onDismiss}
							disabled={isAccepting || isDismissing}
						>
							{isDismissing ? (
								<>
									<Spinner className="size-4" />
									Dismissing...
								</>
							) : (
								<>
									<X
										data-icon="inline-start"
										className="size-4"
									/>
									Dismiss
								</>
							)}
						</Button>
					</div>
				</CardFooter>
			)}
		</Card>
	);
}

// ---------------------------------------------------------------------------
// Edit Suggestion Dialog
// ---------------------------------------------------------------------------

interface EditSuggestionDialogProps {
	campaignId: number;
	suggestion: GroupSuggestion;
	open: boolean;
	onOpenChange: (open: boolean) => void;
	isPending: boolean;
	onSave: (data: AcceptSuggestionRequest) => void;
}

function EditSuggestionDialog({
	campaignId,
	suggestion,
	open,
	onOpenChange,
	isPending,
	onSave,
}: EditSuggestionDialogProps) {
	const [name, setName] = useState(suggestion.name);
	const [description, setDescription] = useState(
		suggestion.description ?? "",
	);
	const [filterRules, setFilterRules] = useState<FilterRules>(
		suggestion.filter_rules,
	);

	// Reset form when dialog opens with different suggestion
	useEffect(() => {
		if (open) {
			setName(suggestion.name);
			setDescription(suggestion.description ?? "");
			setFilterRules(suggestion.filter_rules);
		}
	}, [open, suggestion]);

	function handleSave() {
		const data: AcceptSuggestionRequest = {};

		if (name.trim() !== suggestion.name) {
			data.name = name.trim();
		}
		if (description.trim() !== (suggestion.description ?? "")) {
			data.description = description.trim();
		}

		// Always include filter rules if they have valid entries
		const validRules = filterRules.rules.filter((r) => r.field);
		if (validRules.length > 0) {
			data.filter_rules = { logic: "AND", rules: validRules };
		}

		onSave(data);
	}

	return (
		<Dialog open={open} onOpenChange={onOpenChange}>
			<DialogContent className="sm:max-w-2xl max-h-[85vh] overflow-y-auto">
				<DialogHeader>
					<DialogTitle>Edit Suggestion</DialogTitle>
					<DialogDescription>
						Customize this suggestion before creating the action
						group.
					</DialogDescription>
				</DialogHeader>

				<div className="space-y-4">
					<div className="space-y-2">
						<Label htmlFor="edit-suggestion-name">Name</Label>
						<Input
							id="edit-suggestion-name"
							value={name}
							onChange={(e) => setName(e.target.value)}
							autoFocus
						/>
					</div>
					<div className="space-y-2">
						<Label htmlFor="edit-suggestion-description">
							Description{" "}
							<span className="font-normal text-muted-foreground">
								(optional)
							</span>
						</Label>
						<Textarea
							id="edit-suggestion-description"
							value={description}
							onChange={(e) => setDescription(e.target.value)}
							rows={3}
						/>
					</div>
					<div className="space-y-2">
						<Label>Filter Rules</Label>
						<div className="max-h-[40vh] overflow-y-auto">
							<FilterRuleBuilder
								campaignId={campaignId}
								value={filterRules}
								onChange={setFilterRules}
								showPreview
							/>
						</div>
					</div>
				</div>

				<DialogFooter>
					<Button
						variant="outline"
						onClick={() => onOpenChange(false)}
					>
						Cancel
					</Button>
					<Button
						onClick={handleSave}
						disabled={!name.trim() || isPending}
					>
						{isPending ? (
							<>
								<Spinner className="size-4" />
								Saving...
							</>
						) : (
							"Save & Create Group"
						)}
					</Button>
				</DialogFooter>
			</DialogContent>
		</Dialog>
	);
}
